import { IconLib, Pill, Select, SelectOption, SelectProps, Text, getKey } from '@fold-dev/core'
import React, { ReactElement, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { CommonContext } from './common.provider'
import { useAppStore } from '../../store/app'

const LAST_SELECTED_LABEL = 'LAST_SELECTED_LABEL'
window[LAST_SELECTED_LABEL] = null

export type LabelSelectLabel = {
    id: string | number
    icon?: string
    color?: string
    text: string
}

export type LabelSelectProps = {
    labels: LabelSelectLabel[]
    onAdd?: (label: LabelSelectLabel) => void
    onDelete?: (label: LabelSelectLabel) => void
    footer?: ReactElement | null
    onDismiss?: () => void
}

export const LabelSelect = (props: LabelSelectProps) => {
    const allLabels = useAppStore((state) => state.labels)
    const { labels = [], onAdd, onDelete, onDismiss, footer } = props
    const { availableLabels = [], onLabelFilter } = useContext(CommonContext)
    const selected = useMemo(() => labels.map(({ id }) => id), [labels.length])
    const options: any = useMemo(() => {
        const labels = availableLabels
            .filter((label: LabelSelectLabel) =>
                window[LAST_SELECTED_LABEL] ? window[LAST_SELECTED_LABEL].id != label.id : true
            )
            .map((label: LabelSelectLabel) => ({
                key: label.id,
                label: label.text,
                suffix: <span style={{ width: 10, height: 10, borderRadius: 50, backgroundColor: label.color }} />,
            }))

        return window[LAST_SELECTED_LABEL]
            ? [
                  {
                      key: window[LAST_SELECTED_LABEL].id,
                      label: window[LAST_SELECTED_LABEL].text,
                      suffix: (
                          <span className="f-row">
                              <strong
                                  style={{ color: window[LAST_SELECTED_LABEL].color, paddingRight: 5 }}
                                  className="f-text xs">
                                  RECENT
                              </strong>
                              <span
                                  style={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: 50,
                                      backgroundColor: window[LAST_SELECTED_LABEL].color,
                                  }}
                              />
                          </span>
                      ),
                  },
                  ...labels,
              ]
            : labels
    }, [availableLabels])

    const handleLabelSelect = (option: SelectOption) => {
        // first find it here
        // recent labels might only be available in the master list
        // not necessarily in the filtered/limited list - so search here first
        let availableLabel = allLabels.find((label: any) => label.id == option.key)

        // if it's not there, we can assume it's probably a "new label" with id & key being null
        // if it is then we should find it here - otherwise fuck knows where our label ran off to
        if (!availableLabel) availableLabel = availableLabels.find((label: any) => label.id == option.key)

        // check if the label is currently part of this list (so we need to remove it)
        const label = labels.find((label: any) => label.id == option.key)

        if (!!label) {
            onDelete(label)
        } else {
            if (availableLabel?.id) window[LAST_SELECTED_LABEL] = availableLabel
            onAdd(availableLabel)
        }
    }

    const handleInputKeyDown = (e) => {
        const { isBackspace, isEscape } = getKey(e)
        if (isEscape) onDismiss()
        if (isBackspace && !e.target.value) onDelete(labels[labels.length - 1])
    }

    useEffect(() => {
        onLabelFilter('')
    }, [])

    return (
        <Select
            tagInput
            variant="static"
            noListFocus
            trapFocus
            placeholder="Label..."
            selected={selected}
            options={options}
            filterDelay={500}
            selectListProps={{
                noOptionsComponent: <Text p="var(--f-select-option-padding)">No labels available</Text>,
            }}
            tagInputFieldProps={{ onKeyDown: handleInputKeyDown }}
            onSelect={(option, dismiss, clear) => {
                clear()
                handleLabelSelect(option)
            }}
            onFilter={onLabelFilter}
            footer={footer}
            render={() =>
                labels.map((label: LabelSelectLabel, index: number) => (
                    <Pill
                        subtle
                        key={index}
                        color={label.color}
                        size="sm"
                        suffix={
                            <IconLib
                                icon="x"
                                className="f-buttonize"
                                onClick={() => onDelete(label)}
                            />
                        }>
                        {label.text}
                    </Pill>
                ))
            }
        />
    )
}
